import { Checkbox, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TableContainer, Typography, Slider, Button, Box, Tooltip, Link } from "@mui/material";
import { useEffect, useRef, useState, Fragment, useMemo, useCallback } from "react";
import { IGridcolumn } from "./interface/column.interface";
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import Pagination from '@mui/material/Pagination';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import PauseCircleFilledOutlinedIcon from '@mui/icons-material/PauseCircleFilledOutlined';
import { useAppDispatch } from '../store/hooks';
import { useParams, useNavigate } from 'react-router-dom';
import { setCurrentTestResultDetails } from '../store/resuders/project.reducer';
import { getCurrentProject, getCurrentTestResult, getCurrentTestResultDetails } from '../store/selectors/project.selector';
import { getTestResultList, getTestResultDetails } from '../services/testresults.service';
import { useSelector } from 'react-redux';
import { formatTimestamp } from "../helpers/date.helper";
import { useTranslation } from 'react-i18next';

import { CHANNELS, DIGITAL_STEP_TYPE, LOCATOR_TYPES, RUN_STATUS, STEP_TYPE } from "../types/global.enum";

import { Modal } from "./modal";
import { ActionInfoBody } from "./actionInfoBody";
import { getDigitalApi, getVoiceApi } from "../services/config.service";
import { handleScreenShotsUrl, transcription } from "../services/testcase.service";
import { ISegment, ISegmentLanguage, ISegmentStep } from '../types/interfaces/segment.interface';
import { copyTextToClipboard, getStepTypeDisplayName } from '../helpers/form.helper';
import { orderBy, uniqueId } from 'lodash';
import {
 copyIcon, pauseIcon, playIcon, transIcon, 
    
} from '../assets';
import VoiceChatIcon from '@mui/icons-material/VoiceChat';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { playFailedAudio } from '../services/audio.service';
import { Language } from "@mui/icons-material";
import { WaveformComparisonPlayer } from './WaveformComparisonPlayer';

// Helper interface for segment matches
interface SegmentMatch {
    start_index: number;
    end_index: number;
    matched_text: string;
    segment_text: string;
    similarity: number;
}

interface ContextAlignmentResult {
    threshold_used: number;
    segment_type: string;
    transcribed_text: string;
    expected_prompt: string;
    similarity_score: number;
    segment_matches?: SegmentMatch[];
    transcription_request_id?: string;
    comparison_mode?: string;
    waveform?: {
        passed: boolean;
        max_correlation: number;
        threshold: number;
        all_matches: Array<{
            confidence: number;
            start_time: number;
            end_time: number;
            duration: number;
        }>;
        processing_metrics?: {
            processing_time: number;
            positions_searched: number;
        };
        job_id?: string;
    };
}

// Helper function to get color based on similarity score
const getSimilarityColor = (similarity: number): string => {
    if (similarity >= 1.0) return '#4CAF50';      // Perfect match - green
    if (similarity >= 0.9) return '#8BC34A';      // Excellent - light green
    if (similarity >= 0.8) return '#FFC107';      // Good - amber
    if (similarity >= 0.7) return '#FF9800';      // Fair - orange
    return '#F44336';                              // Poor - red
};

// Helper function to highlight transcript text with segment matches
const highlightTranscriptText = (text: string, matches: SegmentMatch[]): React.ReactNode => {
    if (!matches || matches.length === 0) {
        return <span>{text}</span>;
    }

    // Sort matches by start_index to avoid overlap issues
    const sortedMatches = [...matches].sort((a, b) => a.start_index - b.start_index);
    
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;

    sortedMatches.forEach((match, index) => {
        // Add text before this match
        if (match.start_index > lastIndex) {
            elements.push(
                <span key={`before-${index}`}>
                    {text.substring(lastIndex, match.start_index)}
                </span>
            );
        }

        // Add the highlighted match
        const backgroundColor = getSimilarityColor(match.similarity);
        elements.push(
            <span
                key={`match-${index}`}
                style={{
                    backgroundColor: backgroundColor + '40', // Add transparency
                    border: `1px solid ${backgroundColor}`,
                    borderRadius: '3px',
                    padding: '1px 3px',
                    fontWeight: 'bold'
                }}
                title={`Match: "${match.segment_text}" → "${match.matched_text}" (${(match.similarity * 100).toFixed(1)}%)`}
            >
                {text.substring(match.start_index, match.end_index)}
            </span>
        );

        lastIndex = match.end_index;
    });

    // Add remaining text after last match
    if (lastIndex < text.length) {
        elements.push(
            <span key="after-last">
                {text.substring(lastIndex)}
            </span>
        );
    }

    return <span>{elements}</span>;
};

// Helper function to parse contextAlignmentResult JSON
const parseContextAlignmentResult = (contextAlignmentResultString: string): ContextAlignmentResult | null => {
    try {
        if (!contextAlignmentResultString) return null;

        const jsonStart = contextAlignmentResultString.indexOf('{');
        if (jsonStart >= 0) {
            const jsonString = contextAlignmentResultString.substring(jsonStart);
            return JSON.parse(jsonString);
        }
        return null;
    } catch (e) {
        console.error('Error parsing contextAlignmentResult:', e);
        return null;
    }
};

// Enhanced Listen v2 Results component
const EnhancedListenBetaResults = ({ data, hideTranscriptionDetailsLink = true }: { data: ContextAlignmentResult; hideTranscriptionDetailsLink?: boolean }) => {
    const isMatch = data.similarity_score >= data.threshold_used;
    const statusColor = isMatch ? '#58A148' : 'red';
    const similarityPercentage = (data.similarity_score * 100).toFixed(1);
    const thresholdPercentage = (data.threshold_used * 100).toFixed(1);

    return (
        <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
            {/* Status and Overall Score */}
            <div style={{ margin: '5px 0' }}>
                <span style={{ fontWeight: 'bold' }}>Match Status: </span>
                <span style={{ color: statusColor, fontWeight: 'bold' }}>
                    {isMatch ? 'Match ✓' : 'No Match ✗'}
                </span>
            </div>
            <div style={{ margin: '5px 0' }}>
                <span style={{ fontWeight: 'bold' }}>Overall Score: </span>
                <span style={{ color: statusColor, fontWeight: 'bold' }}>
                    {similarityPercentage}%
                </span>
                <span style={{ color: '#666', marginLeft: '8px' }}>
                    (Threshold: {thresholdPercentage}%)
                </span>
            </div>

            {/* Expected Prompt */}
            <div style={{ margin: '10px 0' }}>
                <span style={{ fontWeight: 'bold' }}>Expected Prompt: </span>
                <span style={{ fontFamily: 'monospace' }}>
                    {data.expected_prompt || 'N/A'}
                </span>
            </div>

            {/* Highlighted Transcribed Text */}
            <div style={{ margin: '10px 0' }}>
                <div style={{ fontWeight: 'bold' }}>Transcribed Text:</div>
                <div style={{ 
                    padding: '8px', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '4px',
                    marginTop: '4px',
                    lineHeight: '1.5'
                }}>
                    {highlightTranscriptText(data.transcribed_text || '', data.segment_matches || [])}
                </div>
            </div>

            {/* Transcription Debug Link */}
            {data.transcription_request_id && (
                <div style={{ margin: '10px 0' }}>
                    {hideTranscriptionDetailsLink ? (
                        <span style={{ 
                            color: '#666666',
                            fontSize: '0.9em'
                        }}>
                            Transcription ID: {data.transcription_request_id}
                        </span>
                    ) : (
                        <Link 
                            href={`http://35.183.46.63:3000/debug-segments?id=${data.transcription_request_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#1976d2',
                                textDecoration: 'none',
                                fontSize: '0.9em'
                            }}
                        >
                            🔍 View Transcription Details ({data.transcription_request_id})
                        </Link>
                    )}
                </div>
            )}

        </div>
    );
};

// Flag allows easily toggling the detailed matches list back on later
const SHOW_WAVEFORM_MATCH_LIST = false;

// Flag to hide the waveform player with reference audio match highlighting
const SHOW_WAVEFORM_WITH_MATCH_HIGHLIGHTING = false;

// Shared function to render contextAlignmentResult for listen steps
const renderContextAlignmentDisplay = (
    betaData: ContextAlignmentResult,
    hideTranscriptionDetailsLink: boolean
): React.ReactNode => {
    // Check if this is waveform comparison mode
    if (betaData.comparison_mode === 'waveform' || betaData.waveform) {
        return <WaveformComparisonResults data={{ waveform: betaData.waveform || betaData }} />;
    }
    // Check if segment_matches exists for enhanced transcription display
    else if (betaData.segment_matches && betaData.segment_matches.length > 0) {
        return <EnhancedListenBetaResults data={betaData} hideTranscriptionDetailsLink={hideTranscriptionDetailsLink} />;
    } else {
        // Fallback to basic display for prompts without wildcards
        const isMatch = betaData.similarity_score >= betaData.threshold_used;
        const statusColor = isMatch ? '#58A148' : 'red';
        const similarityPercentage = (betaData.similarity_score * 100).toFixed(1);
        const thresholdPercentage = (betaData.threshold_used * 100).toFixed(1);

        return (
            <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                <div style={{ margin: '5px 0' }}>
                    <span style={{ fontWeight: 'bold' }}>Match Status: </span>
                    <span style={{ color: statusColor, fontWeight: 'bold' }}>
                        {isMatch ? 'Match ✓' : 'No Match ✗'}
                    </span>
                </div>
                <div style={{ margin: '5px 0' }}>
                    <span style={{ fontWeight: 'bold' }}>Similarity Score: </span>
                    <span style={{ color: statusColor, fontWeight: 'bold' }}>
                        {similarityPercentage}%
                    </span>
                    <span style={{ color: '#666', marginLeft: '8px' }}>
                        (Threshold: {thresholdPercentage}%)
                    </span>
                </div>
                <div style={{ margin: '10px 0' }}>
                    <span style={{ fontWeight: 'bold' }}>Expected Prompt: </span>
                    <span style={{ fontFamily: 'monospace' }}>
                        {betaData.expected_prompt || 'N/A'}
                    </span>
                </div>
                <div style={{ margin: '10px 0' }}>
                    <div style={{ fontWeight: 'bold' }}>Transcribed Text:</div>
                    <div style={{
                        padding: '8px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        marginTop: '4px'
                    }}>
                        {betaData.transcribed_text || 'N/A'}
                    </div>
                </div>

                {/* Transcription Debug Link */}
                {betaData.transcription_request_id && (
                    <div style={{ margin: '10px 0' }}>
                        {hideTranscriptionDetailsLink ? (
                            <span style={{
                                color: '#666666',
                                fontSize: '0.9em'
                            }}>
                                Transcription ID: {betaData.transcription_request_id}
                            </span>
                        ) : (
                            <Link
                                href={`http://35.183.46.63:3000/debug-segments?id=${betaData.transcription_request_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: '#1976d2',
                                    textDecoration: 'none',
                                    fontSize: '0.9em'
                                }}
                            >
                                🔍 View Transcription Details ({betaData.transcription_request_id})
                            </Link>
                        )}
                    </div>
                )}
            </div>
        );
    }
};

// Waveform Comparison Results component
const WaveformComparisonResults = ({ data }: { data: any }) => {
    const waveformData = data?.waveform;
    if (!waveformData) return null;

    const isMatch = waveformData.passed;
    const statusColor = isMatch ? '#58A148' : 'red';
    const correlationPercentage = ((waveformData.max_correlation || 0) * 100).toFixed(1);
    const thresholdPercentage = ((waveformData.threshold || 0) * 100).toFixed(1);
    const topMatch = waveformData.all_matches?.[0];
    const topMatchStart = typeof topMatch?.start_time === 'number' ? topMatch.start_time.toFixed(2) : null;
    const topMatchEnd = typeof topMatch?.end_time === 'number' ? topMatch.end_time.toFixed(2) : null;
    const topMatchRange = topMatchStart !== null && topMatchEnd !== null
        ? `${topMatchStart}s to ${topMatchEnd}s`
        : null;
    const topMatchDuration = typeof topMatch?.duration === 'number' ? topMatch.duration.toFixed(2) : null;

    return (
        <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
            {/* Status and Overall Score */}
            <div style={{ margin: '5px 0' }}>
                <span style={{ fontWeight: 'bold' }}>Match Status: </span>
                <span style={{ color: statusColor, fontWeight: 'bold' }}>
                    {isMatch ? 'Match ✓' : 'No Match ✗'}
                </span>
            </div>
            <div style={{ margin: '5px 0', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'baseline' }}>
                <span>
                    <span style={{ fontWeight: 'bold' }}>Waveform Correlation:</span>{' '}
                    <span style={{ color: statusColor, fontWeight: 'bold' }}>
                        {correlationPercentage}%
                    </span>
                </span>
                <span style={{ color: '#666' }}>
                    (Threshold: {thresholdPercentage}%)
                </span>
                {topMatchRange && (
                    <span style={{ color: '#666' }}>
                        Top match: {topMatchRange}
                        {topMatchDuration && ` (${topMatchDuration}s)`}
                    </span>
                )}
            </div>

            {/* All Matches (Top 5) */}
            {SHOW_WAVEFORM_MATCH_LIST && waveformData.all_matches && waveformData.all_matches.length > 0 && (
                <div style={{ margin: '10px 0' }}>
                    <span style={{ fontWeight: 'bold' }}>Matches Found (Top 5): </span>
                    <div style={{ marginTop: '4px' }}>
                        {waveformData.all_matches.slice(0, 5).map((match: any, index: number) => {
                            const confidencePercent = (match.confidence * 100).toFixed(1);
                            const matchColor = match.confidence >= (waveformData.threshold || 0) ? '#58A148' : '#666';
                            const isAboveThreshold = match.confidence >= (waveformData.threshold || 0);

                            return (
                                <div key={index} style={{
                                    padding: '6px 8px',
                                    border: `1px solid ${matchColor}`,
                                    borderRadius: '4px',
                                    marginBottom: '4px',
                                    backgroundColor: isAboveThreshold ? '#f8fff8' : '#f9f9f9'
                                }}>
                                    <div>
                                        <span style={{ fontWeight: 'bold', color: matchColor }}>
                                            Match {index + 1}: {confidencePercent}%
                                        </span>
                                        {isAboveThreshold && <span style={{ color: '#58A148', marginLeft: '8px' }}>✓</span>}
                                    </div>
                                    <div style={{ fontSize: '0.9em', color: '#666' }}>
                                        {match.start_time?.toFixed(2)}s - {match.end_time?.toFixed(2)}s
                                        ({match.duration?.toFixed(2)}s duration)
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Processing Metrics */}
            {/*
            {waveformData.processing_metrics && (
                <div style={{ margin: '10px 0' }}>
                    <div style={{
                        fontSize: '0.9em',
                        color: '#666'
                    }}>
                        Processing time: {((waveformData.processing_metrics.processing_time || 0) * 1000).toFixed(1)}ms
                        {waveformData.processing_metrics.positions_searched &&
                            `, Positions searched: ${waveformData.processing_metrics.positions_searched}`
                        }
                    </div>
                </div>
            )}
            */}

            {/* Job ID */}
            {/*
            {waveformData.job_id && (
                <div style={{ margin: '10px 0' }}>
                    <span style={{
                        color: '#666666',
                        fontSize: '0.9em'
                    }}>
                        Job ID: {waveformData.job_id}
                    </span>
                </div>
            )}
            */}
        </div>
    );
};

export interface IProps {
    columns: IGridcolumn[];
    data: any;
    selection?: boolean;
    selectionChanged?: (id: string[]) => void;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC' | '';
    sortChanged?: (column: string, direction: string) => void;
    pageChanged?: (page: number) => void;
    totalPages?: number;
    criteria: any;
    hideTranscriptionDetailsLink?: boolean;
}
export const TestResultDetailsGrid = ({
    columns = [],
    data = {},
    selection = false,
    selectionChanged,
    sortChanged,
    sortBy = '',
    sortDirection = 'ASC',
    totalPages = 0,
    pageChanged,
    criteria,
    hideTranscriptionDetailsLink = true
}: IProps) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { projectId, testcaseId, testResultId } = useParams();  // ProjectID and TestCaseID from URL
     const project = useSelector(getCurrentProject);  // Store Project
    const testResultDetails = useSelector(getCurrentTestResultDetails)  // Store Test Result Details
    const [audioPosition, setAudioPosition] = useState<number[]>([]); // State to track audio playback position for each segment
    const [playingAudioIndex, setPlayingAudioIndex] = useState<number | null>(
        null
    ); // Index of the segment with playing audio
    const audioElementsRef = useRef<(HTMLAudioElement | null)[]>([]); // Ref to hold an array of <audio> elements
    const [isFullScreen, setIsFullScreen] = useState(false); // Image/Screenshot Full Screen State
    const [fullImage, setFullImage] = useState(''); // Image/Screenshot Full Screen State
    const [openActionInfo, setOpenActionInfo] = useState<string>('');
    const [clickedStep, setClickedStep] = useState<any>(null);
    const [trans, setTrans] = useState<any>({});
    const [loading, setLoading] = useState(false); // State to track loading
    const [audioUrls, setAudioUrls] = useState<{[key: string]: string}>({});
    const [loadingAudio, setLoadingAudio] = useState<{[key: string]: boolean}>({});

    // Memoized callback for waveform player errors
    const handleWaveformError = useCallback((error: Error) => {
        console.error('Failed to load waveform audio:', error);
    }, []);
    const toggleFullScreen = (url:string) => ()=> {
        setIsFullScreen(!isFullScreen);
        setFullImage(url);
    };
    const [openReminder, setOpenReminder] = useState('')
    const formatDate = (dateTimeString: any) => {
        if (!dateTimeString) return '';

        const date = new Date(dateTimeString);
        const formattedDate = `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
        return formattedDate;
    };

    const formatOutput = (run: any) => {
        let output = '';

        switch (run.stepType) {
            case DIGITAL_STEP_TYPE.LOAD_PAGE: {
                output = 'URL:' + '  ' + `${run.simpleParameters[0]?.value}` + '*' + 'Remark:' + '  ' + `${run.remark || 'N/A'}`
                break;
            }
            case DIGITAL_STEP_TYPE.PAUSE: {
                output = 'Seconds:' + '  ' + `${run.simpleParameters[0]?.value}` + '*' + 'Remark:' + '  ' + `${run.remark || 'N/A'}`
                break;
            }
            case DIGITAL_STEP_TYPE.FIND_ITEM: {
                switch (run.itemLocators[0]?.paramType) {
                    case LOCATOR_TYPES.DISPLAY_TEXT: {
                        output = 'Locator Type: Find by display text' + ' , ' +
                            'Operations: ' + `${run.itemLocators[0]?.matchOp}` + ' , ' +
                            'Display Text: ' + `${run.itemLocators[0]?.value}` + '*' + 'Remark:' + '  ' + `${run.remark || 'N/A'}`
                        break;
                    }
                    case LOCATOR_TYPES.HTML_ATTRIBUTE: {
                        output = 'Locator Type: Find by html attribute' + ' , ' +
                            'Attribute: ' + `${run.itemLocators[0]?.name}` + ' , ' +
                            'Operations: ' + `${run.itemLocators[0]?.matchOp}` + ' , ' +
                            'Attribute Value: ' + `${run.itemLocators[0]?.value}` + '*' + 'Remark:' + '  ' + `${run.remark || 'N/A'}`
                        break;
                    }
                    case LOCATOR_TYPES.HTML_ELEMENT: {
                        output = 'Locator Type: Find by html element' + ' , ' +
                            'Element Name: ' + `${run.itemLocators[0]?.name}` +
                            '*' + 'Remark:' + '  ' + `${run.remark || 'N/A'}`
                        break;
                    }
                }


                break;
            }
            case DIGITAL_STEP_TYPE.CLICK: {
                output = 'Remark:' + '  ' + `${run.remark || 'N/A'}`
                break;
            }
            case DIGITAL_STEP_TYPE.ENTER_TEXT: {
                // Debug log to check values
                console.log('ENTER_TEXT run object:', JSON.stringify(run, null, 2));
                console.log('testResult:', run.testResult);
                console.log('contextAlignmentResult value:', run.latencyStep?.testResult?.contextAlignmentResult);
                
                // Parse the JSON contextAlignmentResult
                let alignmentData = { 
                    isAligned: false,
                    confidence: 0,
                    contextAlignmentCriteria: '',
                    contextAlignmentExample: '',
                    chatbotResponse: '',
                    explanation: ''
                };
                let formattedResult = 'N/A';
                
                try {
                    if (run.latencyStep?.testResult?.contextAlignmentResult) {
                        alignmentData = JSON.parse(run.latencyStep.testResult.contextAlignmentResult);
                        
                        // Format the alignment data for display with better visual structure
                        const statusColor = alignmentData.isAligned ? '#58A148' : 'red';
                        
                        formattedResult = [
                            `<span>Is Aligned: <span style="color:${statusColor};font-weight:bold">${alignmentData.isAligned ? 'Yes' : 'No'}</span></span>`,
                            `Confidence: ${alignmentData.confidence ? (alignmentData.confidence * 100).toFixed(0) + '%' : 'N/A'}`,
                            '',
                            `<b>Criteria:</b>`,
                            alignmentData.contextAlignmentCriteria || 'N/A',
                            '',
                            `<b>Example:</b>`,
                            alignmentData.contextAlignmentExample || 'N/A',
                            '',
                            `<b>Actual Response:</b>`,
                            alignmentData.chatbotResponse || 'N/A',
                            '',
                            `<b>Explanation:</b>`,
                            alignmentData.explanation || 'N/A'
                        ].join('\n');
                    }
                } catch (e) {
                    console.error('Error parsing contextAlignmentResult:', e);
                    formattedResult = run.latencyStep?.testResult?.contextAlignmentResult || 'N/A';
                }
                
                output = run.latencyStep?.testResult?.contextAlignmentResult 
                    ? '<h4 style="margin-top:0;padding-top:0">Context Alignment Result:</h4>' + formattedResult
                    : ''
                break;
            }

            case DIGITAL_STEP_TYPE.SWITCH_FRAME: {
                output = 'Remark:' + '  ' + `${run.remark || 'N/A'}`
                break;
            }
            case DIGITAL_STEP_TYPE.SCREENSHOT: {
                output =!run.testResult?.attachmentUrl ? "#": getDigitalApi().replace('/api/chat/v1','') + `${run.testResult?.attachmentUrl}`;
                break;
            }

        }
        return output;
    }

    const downloadAudio = (attachmentUrl: any | undefined) => {
        if (attachmentUrl) {
            const modifiedVariable = attachmentUrl?.replace("/api/voice/v1", "");
            const desiredURL =
            getVoiceApi() + modifiedVariable;

            // Create a hidden <a> tag to trigger the download
            const downloadLink = document.createElement("a");
            downloadLink.href = desiredURL;
            downloadLink.download = "audio.wav";
            downloadLink.style.display = "none";
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        }
    };

    const playAudio = (attachmentUrl: string | undefined, index: number) => {
        if (attachmentUrl) {
            const modifiedVariable = attachmentUrl?.replace("/api/voice/v1", "");
            const desiredURL = getVoiceApi() + modifiedVariable;

            // Check if the audio element for this segment already exists
            if (!audioElementsRef.current[index]) {
                const audio = new Audio(desiredURL);

                audio.addEventListener("ended", () => {
                    setPlayingAudioIndex(null); // Reset the playing audio index
                });

                audioElementsRef.current[index] = audio;
            }

            const audioElement = audioElementsRef.current[index];

            if (index === playingAudioIndex) {
                // If the audio for the clicked segment is already playing, pause it
                audioElement?.pause();
                setPlayingAudioIndex(null);
            } else {
                // Stop any previously playing audio
                if (playingAudioIndex !== null) {
                    audioElementsRef.current[playingAudioIndex]?.pause();
                }

                // Play the clicked audio
                audioElement?.play();
                setPlayingAudioIndex(index);
            }
        }
    };

    // const loadData = async () => {
    //     const { data: tempData } = await getTestResultList(criteria.projectId);
    //     const { data: testResultData } = await getTestResultList(criteria.projectId, tempData.totalElements || 10, 0, criteria.filter);

    //     const testResultToFetch = testResultId ? testResultId : testResultData?.id;
    //     if (testResultToFetch) {
    //         const { data } = await getTestResultDetails(testResultToFetch);
    //         dispatch(setCurrentTestResultDetails(data));
    //     }
    // };


    // useEffect(() => {
    //     loadData();
    // }, []);

    useEffect(() => {
        // Add event listeners for each audio element
        audioElementsRef.current.forEach((audioElement, index) => {
            if (audioElement) {
                // Time update when moving position
                audioElement.addEventListener("timeupdate", () => {
                    // Update audio position here if needed
                });
                audioElement.addEventListener("ended", () => {
                    setPlayingAudioIndex(null); // Reset the playing audio index
                });
            }
        });

        // Clean up event listeners when the component unmounts
        return () => {
            audioElementsRef.current.forEach((audioElement, index) => {
                if (audioElement) {
                    // Time update when moving position
                    audioElement.removeEventListener("timeupdate", () => {
                        // Update audio position here if needed
                    });
                    audioElement.removeEventListener("ended", () => {
                        setPlayingAudioIndex(null);
                    });
                }
            });
        };
    }, [audioPosition]);


    const isDigital = useMemo(()=> project?.data?.channelType === CHANNELS.DIGITAL ,[project]);

    const showStepDetails = (step : any) => {
        setClickedStep(step);
        setOpenActionInfo(`${new Date().getTime()}`);
    };

    const onStepDetailsModalClose = () => {
        setOpenActionInfo('');
    };


    const downloadImage = useCallback((url: any | undefined, filename: string) => {
        window.open(handleScreenShotsUrl(url, isDigital));
    },[isDigital]);

    const getFailedListenFile = (seg: ISegment, step: ISegmentStep) => async () => {
        setLoading(true); // Show loader
        try {
            if (seg?.testResult?.id && step.testResult?.id) {
                if (!trans[`${seg?.testResult?.id}-${step.testResult?.id}`]) {
                    const { data } = await transcription(seg?.testResult?.id, step.testResult?.id, seg?.defaultLng || 'english');

                    if (data?.code === 'success') {
                        setTrans({
                            ...trans,
                            [`${seg?.testResult?.id}-${step.testResult?.id}`]: data?.data?.transcript,
                        })
                    } else {

                    }
                } else {
                    copyTextToClipboard(trans[`${seg?.testResult?.id}-${step.testResult?.id}`]);
                    if (step.stepType === STEP_TYPE.LISTEN) {
                        setOpenReminder(uniqueId());
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching transcription:', error);
        } finally {
            setLoading(false); // Hide loader
        }
    }

    const getTransIcon = (segId?: number, stepId?: number) => {
        return !trans[`${segId}-${stepId}`] ? transIcon : copyIcon;
    }

    const getTranscription = (segId?: number, stepId?: number) => {
        return trans[`${segId}-${stepId}`] || '';
    }

    return (
        <TableContainer className="test-result-details-container">
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={columns.length + 1}>
                            <div className="test-result-details-text-wrapper">{t('Test Run Details')}</div>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* Custom row for Test Case ID, Test Case Name, and MOS */}
                    <TableRow>
                        <TableCell>{t('Test Run ID')}: {testResultId}</TableCell>
                        <TableCell colSpan={columns.length}>{t('From')}: {formatTimestamp(data?.testResult?.startTime)} to {formatTimestamp(data?.testResult?.endTime)}</TableCell>
                    </TableRow>
                    {/* Custom row for Test Run ID, From, and Duration */}
                    <TableRow>
                        <TableCell>{isDigital ? t('BROWSER'): t('CLID')}: {isDigital ? data?.testResult?.browserType: data?.callerId}</TableCell>
                        {!isDigital && <TableCell colSpan={columns.length-3}>{t('IVR Tel. Number')}: {data?.ivrPhoneNumber}</TableCell>}
                        
                        <TableCell>{t('Duration')}: {
                            (() => {
                                const startTime = new Date(data?.testResult?.startTime);
                                const endTime = new Date(data?.testResult?.endTime);
                                const durationMilliseconds = endTime.getTime() - startTime.getTime();
                                const durationInSeconds = durationMilliseconds / 1000;
                                if (durationInSeconds < 0) {
                                    return `0 secs`;
                                } else {
                                    return !durationInSeconds ? '':`${durationInSeconds.toFixed(2)} secs`;
                                }
                            })()
                        }
                        </TableCell>
                        {!isDigital && <TableCell>{t('MOS')}: {data?.testResult?.voice?.mos ? data.testResult.voice.mos : 0}</TableCell>}
                    </TableRow>
                </TableBody>
                {data?.segments?.map((segment: any, segmentIndex: any) => (
                    orderBy(segment?.steps || [], 'stepSeq')?.map((step: any, stepIndex: any) => (
                        <Fragment key={segmentIndex}>
                            <TableHead>
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1}>
                                        <Typography className="test-result-details-text-wrapper">
                                        {t('Test Segment')}: {segment?.id || segment?.testSegmentId} - {segment?.name}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableHead className="test-result-details-column-header">
                                <TableRow>
                                    <TableCell className="white-background-cell">{t('Step ID')}</TableCell>
                                    <TableCell className="white-background-cell step-type-cell" >{t('Step Type')}</TableCell>
                                    <TableCell className="white-background-cell">{t('Details')}</TableCell>
                                    <TableCell className="white-background-cell">{t('Status')}</TableCell>
                                    <TableCell className="white-background-cell">{t('Prompt Latency')} {'[sec]'}</TableCell>
                                    {!isDigital && <TableCell className="white-background-cell">{t('Duration')}</TableCell>}
                                    {!isDigital && <TableCell className="white-background-cell" style={{ textAlign: 'center' }}>{t('Action')}</TableCell>}
                                </TableRow>

                            </TableHead>
                            <TableBody className="test-result-details-column-body">
                                <TableRow key={stepIndex}>
                                    <TableCell className="light-green-background-cell">{step?.testStepId}</TableCell>
                                    <TableCell className="light-green-background-cell">
                                        {isDigital ?
                                        <Link onClick={()=>{showStepDetails(step)}} className="a-link-black">
                                        {getStepTypeDisplayName(step?.stepType)}
                                        </Link>
                                        :
                                            getStepTypeDisplayName(step?.stepType)
                                        }
                                        </TableCell>
                                    <TableCell className="light-green-background-cell">
                                        <div>

                                            {project?.data?.channelType === CHANNELS.DIGITAL && step?.stepType !== DIGITAL_STEP_TYPE.SCREENSHOT && (
                                                 <>
                                                     {step?.stepType === DIGITAL_STEP_TYPE.ENTER_TEXT ? (
                                                         <div 
                                                            style={{ 
                                                              whiteSpace: 'pre-line',
                                                              wordBreak: 'break-word',
                                                              margin: 0,
                                                              padding: 0
                                                            }} 
                                                            dangerouslySetInnerHTML={{ __html: formatOutput(step) }}
                                                         />
                                                     ) : (
                                                         <div>{formatOutput(step).split('*')[0]}<br /> {formatOutput(step).split('*')[1]}</div>
                                                     )}
                                                 </>
                                            )}

                                            {step?.stepType === DIGITAL_STEP_TYPE.SCREENSHOT && (
                                                <>
                                                 {step.testResult !== null && (<div className="image-container"> <a className="underscore" href={formatOutput(step)}><img
                                                            src={formatOutput(step)}
                                                            alt="screenshot"
                                                            className="image-clickable"
                                                            onClick={toggleFullScreen('')}
                                                        /></a></div>)}
                                                 <div>Remark: {step?.remark || 'N/A'}</div>
                                                </>
                                            )}


                                            {step?.stepType.toLowerCase() === "listen" && (
                                                (() => {
                                                    // Check if contextAlignmentResult exists and use enhanced display
                                                    if (step?.testResult?.contextAlignmentResult) {
                                                        const parsedResult = parseContextAlignmentResult(step.testResult.contextAlignmentResult);
                                                        if (parsedResult) {
                                                            return (
                                                                <div style={{ marginTop: '10px' }}>
                                                                    {renderContextAlignmentDisplay(parsedResult, hideTranscriptionDetailsLink)}
                                                                </div>
                                                            );
                                                        }
                                                    }

                                                    // Fallback to original display logic if no contextAlignmentResult
                                                    if (step?.testResult?.status === RUN_STATUS.SUCCESS) {
                                                        return (
                                                            <>
                                                                <div>{step?.prompt}</div>
                                                                {(step?.listenSubStep || []).map((c:any)=>(<div className="mt-16">({c.name}){c.prompt}</div>))}
                                                                <div style={{ marginTop: '20px' }}>
                                                                    [{step?.promptLatencyMin},{step?.promptLatencyMax}] {(step?.testResult?.voice?.utterance) ? (step?.testResult?.voice?.utterance) : '<Utterance not recognized>'} [{step?.incompleteTimeout},{step?.completeTimeout}]
                                                                </div>
                                                            </>
                                                        );
                                                    } else if (step?.testResult?.status === RUN_STATUS.FAILED) {
                                                        return (
                                                            <>
                                                                <div>{step?.prompt}</div>
                                                                {(step?.listenSubStep || []).map((c: any) => (
                                                                    <div className="mt-16">({c.name}) {c.prompt}</div>
                                                                ))}

                                                                {/* Display utterance from test result if available */}
                                                                {step?.testResult?.voice?.utterance && (
                                                                    <div style={{ marginTop: '20px' }}>
                                                                        [{step?.promptLatencyMin}, {step?.promptLatencyMax}]{' '}
                                                                        {step?.testResult?.voice?.utterance} [
                                                                        {step?.incompleteTimeout}, {step?.completeTimeout}]
                                                                    </div>
                                                                )}

                                                                {getTranscription(segment.testResult?.id, step.testResult?.id) && (
                                                                    <div
                                                                        className="prompt flex no-match"
                                                                        style={{
                                                                            marginTop: '20px',
                                                                            padding: '10px',
                                                                            border: '1px solid #ccc',
                                                                            borderRadius: '4px',
                                                                            maxWidth: '600px', // Adjust the width
                                                                            wordWrap: 'break-word', // Ensure long text wraps
                                                                            marginLeft: 0, // Add some space at the bottom
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        [Transcript] {getTranscription(segment.testResult?.id, step.testResult?.id)}
                                                                    </div>
                                                                )}
                                                                <div className="latency fail-color flex-center gap-8" style={{ marginLeft: -50, cursor: 'pointer' }}>
                                                                    <Tooltip title={loading ? 'Loading...' : 'Utterance not recognized'}>
                                                                        {loading ? (
                                                                            <div className="moving-dots">
                                                                                <span>.</span>
                                                                                <span>.</span>
                                                                                <span>.</span>
                                                                            </div>
                                                                        ) : (
                                                                            <VoiceChatIcon onClick={getFailedListenFile(segment, step)} />
                                                                        )}
                                                                    </Tooltip>
                                                                </div>
                                                            </>
                                                        );
                                                    }

                                                    return ""; // Empty for other cases
                                                })()
                                            )}

                                            {step?.stepType.toLowerCase() === "hang up" && (
                                                <>
                                                    {/* Empty Details*/}
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "pause" && (
                                                <>
                                                    {/* Empty Details */}
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "listen anything" && (
                                                // <>
                                                //     <div>{step?.prompt}</div>
                                                //     <div style={{ marginTop: '20px' }}>
                                                //         [{step?.promptLatencyMin},{step?.promptLatencyMax}] {step?.testResult?.voice?.utterance} [{step?.incompleteTimeout},{step?.completeTimeout}]
                                                //     </div>
                                                // </>
                                                step?.testResult?.status === RUN_STATUS.SUCCESS ? (
                                                    <>
                                                        <div>{step?.prompt}</div>
                                                        {(step?.listenSubStep || []).map((c:any)=>(<div className="mt-16">({c.name}){c.prompt}</div>))}
                                                        <div style={{ marginTop: '20px' }}>
                                                            [{step?.promptLatencyMin},{step?.promptLatencyMax}] {(step?.testResult?.voice?.utterance) ? (step?.testResult?.voice?.utterance) : '<Utterance not recognized>'} [{step?.incompleteTimeout},{step?.completeTimeout}]
                                                        </div>
                                                    </>
                                                ):step?.testResult?.status === RUN_STATUS.FAILED ? (
                                                    <>
                                                        <div>{step?.prompt}</div>
                                                        {(step?.listenSubStep || []).map((c: any) => (
                                                            <div className="mt-16">({c.name}) {c.prompt}</div>
                                                            ))}

                                                            {/* Display utterance from test result if available */}
                                                            {step?.testResult?.voice?.utterance && (
                                                            <div style={{ marginTop: '20px' }}>
                                                            [{step?.promptLatencyMin}, {step?.promptLatencyMax}]{' '}
                                                            {step?.testResult?.voice?.utterance} [
                                                            {step?.incompleteTimeout}, {step?.completeTimeout}]
                                                            </div>
                                                            )}

                                                            {getTranscription(segment.testResult?.id, step.testResult?.id) && (
                                                            <div
                                                                className="prompt flex no-match"
                                                                style={{
                                                                marginTop: '20px',
                                                                padding: '10px',
                                                                border: '1px solid #ccc',
                                                                borderRadius: '4px',
                                                                maxWidth: '600px', // Adjust the width
                                                                wordWrap: 'break-word', // Ensure long text wraps
                                                                marginLeft: 0, // Add some space at the bottom
                                                                cursor: 'pointer',
                                                                }}
                                                            >
                                                                [Transcript] {getTranscription(segment.testResult?.id, step.testResult?.id)}
                                                            </div>
                                                            )}
                                                         <div className="latency fail-color flex-center gap-8" style={{ marginLeft: -50, cursor: 'pointer' }}>
                                                            <Tooltip title={loading ? 'Loading...' : 'Utterance not recognized'}>
                                                            {loading ? (
                                                            <div className="moving-dots">
                                                                <span>.</span>
                                                                <span>.</span>
                                                                <span>.</span>
                                                            </div>
                                                            ) : (
                                                            <VoiceChatIcon onClick={getFailedListenFile(segment, step)} />
                                                            )}
                                                        </Tooltip>
                                                        </div>

                                                    </>
                                                ): ""
                                            )}
                                            
                                            {step?.stepType.toLowerCase() === "listen context alignment" && (
                                                <>
                                                    <div style={{ marginTop: '10px' }}>
                                                        {(() => {
                                                            // Parse the JSON contextAlignmentResult
                                                            let alignmentData = {
                                                                expected_utterance_description: '',
                                                                is_aligned: false,
                                                                confidence: 0,
                                                                transcribed_utterance: '',
                                                                expected_utterance: '',
                                                                explanation: ''
                                                            };
                                                            
                                                            try {
                                                                if (step?.testResult?.contextAlignmentResult) {
                                                                    const jsonStart = step.testResult.contextAlignmentResult.indexOf('{');
                                                                    if (jsonStart >= 0) {
                                                                        const jsonString = step.testResult.contextAlignmentResult.substring(jsonStart);
                                                                        alignmentData = JSON.parse(jsonString);
                                                                        
                                                                        // Format the alignment data for display with better visual structure
                                                                        const statusColor = alignmentData.is_aligned ? '#58A148' : 'red';
                                                                        
                                                                        return (
                                                                            <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                                                                                <div style={{ margin: '5px 0' }}>
                                                                                    <span style={{ fontWeight: 'bold' }}>Alignment Status: </span>
                                                                                    <span style={{ color: statusColor, fontWeight: 'bold' }}>
                                                                                        {alignmentData.is_aligned ? 'Aligned' : 'Not Aligned'}
                                                                                    </span>
                                                                                </div>
                                                                                <div style={{ margin: '5px 0' }}>
                                                                                    <span style={{ fontWeight: 'bold' }}>Confidence: </span>
                                                                                    {(alignmentData.confidence * 100).toFixed(0)}%
                                                                                </div>
                                                                                <div style={{ margin: '10px 0' }}>
                                                                                    <div style={{ fontWeight: 'bold' }}>Expected Response Description:</div>
                                                                                    <div>{alignmentData.expected_utterance_description || 'N/A'}</div>
                                                                                </div>
                                                                                <div style={{ margin: '10px 0' }}>
                                                                                    <div style={{ fontWeight: 'bold' }}>Transcribed Response:</div>
                                                                                    <div>{alignmentData.transcribed_utterance || 'N/A'}</div>
                                                                                </div>
                                                                                <div style={{ margin: '10px 0' }}>
                                                                                    <div style={{ fontWeight: 'bold' }}>Expected Response Example:</div>
                                                                                    <div>{alignmentData.expected_utterance || 'N/A'}</div>
                                                                                </div>
                                                                                <div style={{ margin: '10px 0' }}>
                                                                                    <div style={{ fontWeight: 'bold' }}>Explanation:</div>
                                                                                    <div>{alignmentData.explanation || 'N/A'}</div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing contextAlignmentResult:', e);
                                                            }
                                                            
                                                            return (
                                                                <div>
                                                                    {step?.testResult?.contextAlignmentResult || 'No context alignment result available'}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "listen beta" && (
                                                <>
                                                    <div style={{ marginTop: '10px' }}>
                                                        {(() => {
                                                            // Parse the JSON contextAlignmentResult for listen_beta
                                                            let betaData: ContextAlignmentResult = {
                                                                threshold_used: 0,
                                                                segment_type: '',
                                                                transcribed_text: '',
                                                                expected_prompt: '',
                                                                similarity_score: 0
                                                            };
                                                            
                                                            try {
                                                                if (step?.testResult?.contextAlignmentResult) {
                                                                    const jsonStart = step.testResult.contextAlignmentResult.indexOf('{');
                                                                    if (jsonStart >= 0) {
                                                                        const jsonString = step.testResult.contextAlignmentResult.substring(jsonStart);
                                                                        betaData = JSON.parse(jsonString);

                                                                        // Check if this is waveform comparison mode
                                                                        if (betaData.comparison_mode === 'waveform' || betaData.waveform) {
                                                                            const audioKey = `audio_${step?.testResult?.id}`;
                                                                            const waveformData = betaData.waveform || betaData;
                                                                            const allMatches = waveformData?.all_matches || [];
                                                                            const threshold = waveformData?.threshold || 0.7;

                                                                            // Fetch audio if not already cached
                                                                            if (!audioUrls[audioKey] && !loadingAudio[audioKey] && step?.testResult?.id) {
                                                                                setLoadingAudio(prev => ({...prev, [audioKey]: true}));
                                                                                playFailedAudio(step.testResult.id).then(audioUrl => {
                                                                                    if (audioUrl && audioUrl !== '') {
                                                                                        setAudioUrls(prev => ({...prev, [audioKey]: audioUrl}));
                                                                                    }
                                                                                    setLoadingAudio(prev => ({...prev, [audioKey]: false}));
                                                                                }).catch(err => {
                                                                                    console.error('Failed to fetch audio for step', step.testResult.id, err);
                                                                                    setLoadingAudio(prev => ({...prev, [audioKey]: false}));
                                                                                });
                                                                            }

                                                                            return (
                                                                                <>
                                                                                    <WaveformComparisonResults data={{ waveform: betaData.waveform || betaData }} />
                                                                                    {/* Waveform Player */}
                                                                                    {SHOW_WAVEFORM_WITH_MATCH_HIGHLIGHTING && audioUrls[audioKey] && audioUrls[audioKey] !== '' && allMatches.length > 0 && (
                                                                                        <Box sx={{
                                                                                            width: 'calc(100% + 32px)',
                                                                                            marginLeft: '-16px',
                                                                                            marginRight: '-16px',
                                                                                            marginTop: '16px'
                                                                                        }}>
                                                                                            <WaveformComparisonPlayer
                                                                                                audioUrl={audioUrls[audioKey]}
                                                                                                matches={allMatches}
                                                                                                threshold={threshold}
                                                                                                onError={handleWaveformError}
                                                                                            />
                                                                                        </Box>
                                                                                    )}
                                                                                    {/* Loading indicator */}
                                                                                    {loadingAudio[audioKey] && (
                                                                                        <Box sx={{
                                                                                            marginTop: '16px',
                                                                                            padding: '20px',
                                                                                            backgroundColor: '#f3f4f6',
                                                                                            borderRadius: '6px',
                                                                                            textAlign: 'center'
                                                                                        }}>
                                                                                            <Typography variant="body2" color="textSecondary">
                                                                                                Loading waveform audio...
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    )}
                                                                                </>
                                                                            );
                                                                        }
                                                                        // Check if segment_matches exists for enhanced transcription display
                                                                        else if (betaData.segment_matches && betaData.segment_matches.length > 0) {
                                                                            return <EnhancedListenBetaResults data={betaData} hideTranscriptionDetailsLink={hideTranscriptionDetailsLink} />;
                                                                        } else {
                                                                            // Fallback to basic display for prompts without wildcards
                                                                            const isMatch = betaData.similarity_score >= betaData.threshold_used;
                                                                            const statusColor = isMatch ? '#58A148' : 'red';
                                                                            const similarityPercentage = (betaData.similarity_score * 100).toFixed(1);
                                                                            const thresholdPercentage = (betaData.threshold_used * 100).toFixed(1);
                                                                            
                                                                            return (
                                                                                <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                                                                                    <div style={{ margin: '5px 0' }}>
                                                                                        <span style={{ fontWeight: 'bold' }}>Match Status: </span>
                                                                                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                                                                                            {isMatch ? 'Match ✓' : 'No Match ✗'}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div style={{ margin: '5px 0' }}>
                                                                                        <span style={{ fontWeight: 'bold' }}>Similarity Score: </span>
                                                                                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                                                                                            {similarityPercentage}%
                                                                                        </span>
                                                                                        <span style={{ color: '#666', marginLeft: '8px' }}>
                                                                                            (Threshold: {thresholdPercentage}%)
                                                                                        </span>
                                                                                    </div>
                                                                                    <div style={{ margin: '10px 0' }}>
                                                                                        <span style={{ fontWeight: 'bold' }}>Expected Prompt: </span>
                                                                                        <span style={{ fontFamily: 'monospace' }}>
                                                                                            {betaData.expected_prompt || 'N/A'}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div style={{ margin: '10px 0' }}>
                                                                                        <div style={{ fontWeight: 'bold' }}>Transcribed Text:</div>
                                                                                        <div style={{ 
                                                                                            padding: '8px', 
                                                                                            border: '1px solid #e0e0e0', 
                                                                                            borderRadius: '4px',
                                                                                            marginTop: '4px'
                                                                                        }}>
                                                                                            {betaData.transcribed_text || 'N/A'}
                                                                                        </div>
                                                                                    </div>

                                                                                    {/* Transcription Debug Link */}
                                                                                    {betaData.transcription_request_id && (
                                                                                        <div style={{ margin: '10px 0' }}>
                                                                                            {hideTranscriptionDetailsLink ? (
                                                                                                <span style={{ 
                                                                                                    color: '#666666',
                                                                                                    fontSize: '0.9em'
                                                                                                }}>
                                                                                                    Transcription ID: {betaData.transcription_request_id}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <Link 
                                                                                                    href={`http://35.183.46.63:3000/debug-segments?id=${betaData.transcription_request_id}`}
                                                                                                    target="_blank"
                                                                                                    rel="noopener noreferrer"
                                                                                                    style={{ 
                                                                                                        color: '#1976d2',
                                                                                                        textDecoration: 'none',
                                                                                                        fontSize: '0.9em'
                                                                                                    }}
                                                                                                >
                                                                                                    🔍 View Transcription Details ({betaData.transcription_request_id})
                                                                                                </Link>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        }
                                                                    }
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing listen_beta contextAlignmentResult:', e);
                                                            }
                                                            
                                                            return (
                                                                <div>
                                                                    {step?.testResult?.contextAlignmentResult || 'No listen v2 result available'}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "play audio" && (
                                                <>
                                                    {/* Render Play Audio-specific content here */}
                                                    <div>
                                                        {step?.libraryItemIds?.map((item: any, index: any) => (
                                                            <div key={index}>{item.name}</div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}


                                            {step?.stepType.toLowerCase() === "record audio" && (
                                                <>
                                                    {/* Render Record Audio-specific content here */}
                                                    <div>{step?.recordingHint}</div>
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "speak" && (
                                                <>
                                                    <div>{step?.messageToPlay}</div>
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "va - transfer call to agent" && (
                                                <>
                                                    {/* Render va - transfer call to agent-specific content here */}
                                                    <div>{step?.agentXferActionQueueName}</div>
                                                    <div>{step?.agentXferActionAgentID}</div>
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "va - screenshot" && (
                                                <>
                                                    {/* Render va - screenshot-specific content here */}
                                                    <div className={`image-container ${isFullScreen ? 'full-screen' : ''}`}>
                                                        {isFullScreen && (
                                                            <button className="close-button" onClick={toggleFullScreen('')}>
                                                                <strong>X</strong>
                                                            </button>
                                                        )}
                                                        {fullImage && <img src={fullImage} />}
                                                        <img
                                                            src={handleScreenShotsUrl(step?.testResult?.voice?.agentResponse?.screenshot, isDigital)}
                                                            alt="screenshot"
                                                            className="image-clickable"
                                                            onClick={toggleFullScreen(handleScreenShotsUrl(step?.testResult?.voice?.agentResponse?.screenshot, isDigital))}
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "va - mouse action" && (
                                                <>
                                                    {/* Render va - mouse action-specific content here */}
                                                    <div>{step?.mouseControlActionElementName}</div>
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "va - get call data" && (
                                                <>
                                                    {/* Render va - get call data-specific content here */}
                                                    <div>
                                                        {Object.keys(step?.participantDataActionAttributeNames || {}).map((key) => (
                                                            <div key={key}>
                                                                <strong>{key}:</strong> {step.participantDataActionAttributeNames[key]}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}


                                            {step?.stepType.toLowerCase() === "va - get screen data" && (
                                                <>
                                                    {/* Render va - get screen data-specific content here */}
                                                    <div>{step?.testResult?.voice?.agentResponse?.text || step?.screenDataActionExpectedValue}</div>
                                                </>
                                            )}

                                            {step?.stepType.toLowerCase() === "va - enter screen data" && (
                                                <>
                                                    {/* Render va - enter screen data-specific content here */}
                                                    <div>{step?.fillScreenDataActionValue}</div>
                                                </>
                                            )}
                                        </div>

                                    </TableCell>
                                    <TableCell className="light-green-background-cell"
                                        style={{
                                            color:
                                                step?.testResult?.status === "PASS" ? '#58A148' :
                                                step?.testResult?.status === "FAILED" || step?.testResult?.status === "TIMEOUT_STOPPED" ? 'red' :
                                                        'grey'
                                        }}>
                                        {step?.testResult?.status ? step?.testResult?.status : 'NOT RUN'}
                                    </TableCell>
                                    <TableCell className="light-green-background-cell">
                                        {project?.data?.channelType === CHANNELS.DIGITAL && (
                                                <>
                                                {step?.latencyStep?.testResult?.latency || 0}
                                                </>
                                        )}
                                        {project?.data?.channelType === CHANNELS.VOICE && !["hangup", "pause", "play audio", "speak", "va - transfer call to agent", "va - screenshot", "va - get call data"].includes(
                                            step?.stepType.toLowerCase()
                                        ) && (
                                                <>
                                                  {((step?.testResult?.voice?.latency || step?.promptLatencyMax || 0)/1000).toFixed(2)}
                                                </>
                                            )}

                                    </TableCell>
                                    {!isDigital && <TableCell className="light-green-background-cell">
                                        {step?.stepType.toLowerCase() === "listen" && (
                                            <>
                                                {step?.testResult?.voice?.recordingDuration ? (step?.testResult?.voice.recordingDuration / 1000).toFixed(2) + ' sec'
                                                    : '0 sec'}
                                                <br />
                                                {step?.testResult?.voice?.confidence?.toFixed(2)  || step?.promptConfidenceLevel?.toFixed(2) || '--'}
                                            </>
                                        )}
                                        {step?.stepType.toLowerCase() === "va - transfer call to agent" && (
                                            <>
                                                {step?.testResult?.voice?.latency ? (step?.testResult?.voice?.latency / 1000).toFixed(2) + ' sec'
                                                    : '0 sec'}
                                            </>
                                        )}
                                        {["pause", "listen anything", "listen context alignment", "listen beta", "play audio", "record audio", "speak", "va - screenshot", "va - mouse action", "va - get call data", "va - get screen data", "va - enter screen data"].includes((step?.stepType).toLowerCase()) && (
                                            <>
                                                {(step?.testResult?.startTime && step?.testResult?.endTime) ? (
                                                    (
                                                        (new Date(step.testResult.endTime).getTime() - new Date(step.testResult.startTime).getTime()) / 1000
                                                    ).toFixed(2) + ' sec'
                                                ) : '0 sec'}
                                            </>
                                        )}

                                    </TableCell>}

                                    {!isDigital && <TableCell className="light-green-background-cell">
                                        {!["hangup", "pause", "play audio", "speak", "va - get call data"].includes(
                                            step?.stepType.toLowerCase()
                                        ) && (
                                                <>
                                                    <div className="test-result-details-action-container">
                                                        <div className="action-buttons">
                                                            {["va - transfer call to agent", "va - screenshot", "va - mouse action", "va - get screen data", "va - enter screen data"].includes(
                                                                step?.stepType.toLowerCase()) ?
                                                                step?.testResult?.voice?.agentResponse?.screenshot && (<Tooltip title="Download Screenshot">
                                                                    <FileDownloadOutlinedIcon
                                                                        onClick={() => {
                                                                            downloadImage(step?.testResult?.voice?.agentResponse?.screenshot, "screenshot");
                                                                        }}
                                                                        style={{ fontSize: '35px', cursor: 'pointer' }}
                                                                    />
                                                                </Tooltip>
                                                                ) :
                                                                step?.testResult?.attachmentUrl && (
                                                                    <Tooltip title="Download Audio">
                                                                        <FileDownloadOutlinedIcon
                                                                            onClick={() => {
                                                                                downloadAudio(step?.testResult?.attachmentUrl);
                                                                            }}
                                                                            style={{ fontSize: '35px', cursor: 'pointer' }}
                                                                        />
                                                                    </Tooltip>

                                                                )
                                                            }
                                                            {["listen", "listen anything", "listen context alignment", "listen beta", "play audio", "record audio", "speak"].includes(
                                                                step?.stepType.toLowerCase()
                                                            ) && (
                                                                    <>
                                                                        {/* {playingAudioIndex === segmentIndex ? (
                                                                            <Tooltip title="Pause Audio">
                                                                                <PauseCircleFilledOutlinedIcon
                                                                                    onClick={() => {
                                                                                        playAudio(step?.testResult?.attachmentUrl, segmentIndex);
                                                                                    }}
                                                                                    style={{ fontSize: '30px', cursor: 'pointer' }}
                                                                                />
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Tooltip title="Play Audio">
                                                                                <PlayCircleFilledWhiteOutlinedIcon
                                                                                    onClick={() => {
                                                                                        playAudio(step?.testResult?.attachmentUrl, segmentIndex);
                                                                                    }}
                                                                                    style={{ fontSize: '30px', cursor: 'pointer' }}
                                                                                />
                                                                            </Tooltip>
                                                                        )} */}
                                                                        <audio controls key={step?.testResult?.id || `${segmentIndex}-${stepIndex}`}>
                                                                            <source src={getVoiceApi() + (step?.testResult?.attachmentUrl)?.replace("/api/voice/v1", "")} type="audio/wav" />
                                                                        </audio>
                                                                    </>
                                                                )}
                                                        </div>


                                                        {["listen", "listen anything", "listen context alignment", "listen beta", "play audio", "record audio", "speak", "va - transfer call to agent"].includes(
                                                            step?.stepType.toLowerCase()
                                                        ) && (
                                                                <>
                                                                    {/* Audio Slider Component */}
                                                                </>
                                                                // <>
                                                                //     <Box className="play-audio-slider">
                                                                //         {/* Audio Current Position */}
                                                                //         <Box className="audio-position" >
                                                                //             <span>{audioPosition[segmentIndex]?.toFixed(2) || '0.00'}</span>
                                                                //         </Box>

                                                                //         <Slider
                                                                //             className="slider"
                                                                //             // Set the value of the Slider to the audioPosition for this segment
                                                                //             value={audioPosition[segmentIndex] || 0}
                                                                //             // Handle slider changes
                                                                //             onChange={(event, newValue) => {
                                                                //                 if (audioElementsRef.current[segmentIndex]) {
                                                                //                     // Pause the audio when the slider is moved
                                                                //                     audioElementsRef.current[segmentIndex]?.pause();
                                                                //                     // Update the audio playback position for this segment
                                                                //                     const newPosition = [...audioPosition];
                                                                //                     newPosition[segmentIndex] = newValue as number;
                                                                //                     setAudioPosition(newPosition);
                                                                //                 }
                                                                //             }}
                                                                //             // Handle slider change complete
                                                                //             onChangeCommitted={(event, newValue) => {
                                                                //                 if (audioElementsRef.current[segmentIndex]) {
                                                                //                     const audioElement = audioElementsRef.current[segmentIndex];
                                                                //                     if (audioElement) {
                                                                //                         // Seek the audio to the new position for this segment
                                                                //                         audioElement.currentTime = newValue as number;
                                                                //                         // Resume playing the audio when the slider change is complete
                                                                //                         audioElement.play().catch((error) => {
                                                                //                             console.error("Error resuming audio playback:", error);
                                                                //                         });
                                                                //                     }
                                                                //                 }
                                                                //             }}
                                                                //             min={0}
                                                                //             max={
                                                                //                 audioElementsRef.current[segmentIndex]
                                                                //                     ? audioElementsRef.current[segmentIndex]?.duration
                                                                //                     : 0
                                                                //             }
                                                                //         />

                                                                //         {/* Audio Length */}
                                                                //         <Box className="audio-duration" >
                                                                //             <span>
                                                                //                 {audioElementsRef.current[segmentIndex]
                                                                //                     ? audioElementsRef.current[segmentIndex]?.duration.toFixed(2)
                                                                //                     : '0.00'}
                                                                //             </span>
                                                                //         </Box>
                                                                //     </Box>
                                                                // </>
                                                            )}
                                                    </div>
                                                </>
                                            )}

                                    </TableCell>}

                                </TableRow>
                            </TableBody>
                        </Fragment>
                    ))
                ))}


            </Table>
            <Modal
                isOpen={openActionInfo}
                title={`Action Type Info - ${clickedStep?.stepType || ''}`}
                small={true}
                onClosed={onStepDetailsModalClose}
                primaryButton="Close"
                body={<ActionInfoBody step={clickedStep} stepType={clickedStep?.stepType || ''} onClose={onStepDetailsModalClose}/>}
                
            />
             <Modal
                isOpen={openReminder}
                title="Reminder"
                small={true}
                body={<div className='reminder'><CheckCircleOutlineIcon style={{ fontSize: '53px', color: '#58A148' }} /> <br />The Transcript has been copied to the clipboard.<br />
                    You can apply copied text to segment editor now.</div>}
                deleteConfirmation={false}
            />
        </TableContainer>
    );
}
